import SaveAltRoundedIcon from "@mui/icons-material/SaveAltRounded";
import { Button, LinearProgress, Stack, Typography } from "@mui/material";
import saveAs from "file-saver";
import { buildAccessCheckReport } from "../../auth/accessCheck/buildAccessCheckReport";
import ClassificationBullets from "../../auth/accessCheck/ClassificationBullets";
import type { DocumentFailureOrigin } from "../../auth/accessCheck/judgeDocumentFailure";
import type { BucketClassification, ConnectionCheckResult, DocumentLoadFailure } from "../../auth/accessCheck/types";
import type { PdfBlobCheckState } from "../../services/pdfPreview/pdfBlobCheckTypes";
import PreviewStatusDialog, { previewFailedTitle } from "./PreviewStatusDialog";

interface Props {
  state: PdfBlobCheckState;
  email?: string;
  onClose: () => void;
}

interface ResultsBodyProps {
  classification: BucketClassification;
  checks: ConnectionCheckResult[];
  documentFailureOrigin?: DocumentFailureOrigin;
  loadFailure?: DocumentLoadFailure;
  email?: string;
}

const ResultsBody = ({ classification, checks, documentFailureOrigin, loadFailure, email }: ResultsBodyProps) => {
  const handleDownload = () => {
    const report = buildAccessCheckReport({
      checks,
      classification,
      email,
      documentFailureOrigin,
      documentLoadError: loadFailure,
    });
    saveAs(report.blob, report.filename);
  };

  return (
    <Stack spacing={1.5}>
      <Typography>
        We couldn&apos;t load this document, so we checked whether this device can reach Entrilia document storage.
      </Typography>
      {classification.conclusion && <Typography variant="subtitle2">{classification.conclusion}</Typography>}
      <Typography>{classification.suggestion}</Typography>
      <ClassificationBullets bullets={classification.bullets} />
      <Button variant="contained" color="primary" startIcon={<SaveAltRoundedIcon />} onClick={handleDownload} fullWidth>
        Download details
      </Button>
    </Stack>
  );
};

const titleOf = (state: PdfBlobCheckState): string => {
  switch (state.kind) {
    case "retrying":
      return "Still loading this document";
    case "checking":
      return "Checking document access";
    default:
      return previewFailedTitle;
  }
};

const PdfBlobCheckResultDialog = ({ state, email, onClose }: Props) => {
  // Retrying stays closable — Close cancels the retries.
  const isChecking = state.kind === "checking";

  return (
    <PreviewStatusDialog title={titleOf(state)} onClose={onClose} closeDisabled={isChecking}>
      {state.kind === "retrying" && (
        <Stack spacing={2}>
          <Typography color="textSecondary">
            This document is taking longer than usual to load. Retrying... (attempt {state.attempt} of{" "}
            {state.maxAttempts})
          </Typography>
          <LinearProgress />
        </Stack>
      )}

      {state.kind === "checking" && (
        <Stack spacing={2}>
          <Typography color="textSecondary">
            We couldn&apos;t load this document. Checking whether this device can reach Entrilia document storage...
          </Typography>
          <LinearProgress />
        </Stack>
      )}

      {state.kind === "results" && (
        <ResultsBody
          classification={state.classification}
          checks={state.checks}
          documentFailureOrigin={state.documentFailureOrigin}
          loadFailure={state.loadFailure}
          email={email}
        />
      )}

      {state.kind === "failed" && (
        <Typography color="textSecondary">
          We couldn&apos;t complete the check on this device. Please try again later, or contact your fund manager if
          the document still won&apos;t open.
        </Typography>
      )}
    </PreviewStatusDialog>
  );
};

export default PdfBlobCheckResultDialog;
